import React from "react";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import {
  DateRangePicker,
  DateRangePickerProps,
} from "@mui/x-date-pickers-pro/DateRangePicker";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { PickersShortcutsItem } from "@mui/x-date-pickers/PickersShortcuts";
import { useTheme } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

import "@/utils/dayjs-config";

dayjs.extend(utc);

export type FilterDateRange = DateRange<Dayjs>;

/**
 * Converts the calendar days selected in the picker into inclusive UTC query
 * boundaries. Formatting first is deliberate: calling `.utc()` on a local
 * Dayjs value can move a BST calendar date into the previous UTC day.
 */
export const toUtcDateRangeBounds = (
  value: FilterDateRange
): [Date | null, Date | null] => [
  value[0]
    ? dayjs.utc(value[0].format("YYYY-MM-DD")).startOf("day").toDate()
    : null,
  value[1]
    ? dayjs.utc(value[1].format("YYYY-MM-DD")).endOf("day").toDate()
    : null,
];

type FilterDateRangePickerProps = Omit<
  DateRangePickerProps,
  "format" | "label" | "onChange" | "timezone" | "value"
> & {
  label?: string;
  onChange: (value: FilterDateRange) => void;
  value: FilterDateRange;
};

const startOfToday = () => dayjs().utc().startOf("day");

export const dateRangeShortcuts: PickersShortcutsItem<FilterDateRange>[] = [
  {
    label: "Today",
    getValue: () => {
      const today = startOfToday();
      return [today, today];
    },
  },
  {
    label: "Yesterday",
    getValue: () => {
      const yesterday = startOfToday().subtract(1, "day");
      return [yesterday, yesterday];
    },
  },
  {
    label: "This Week",
    getValue: () => {
      const today = startOfToday();
      return [today.startOf("week"), today.endOf("week")];
    },
  },
  {
    label: "Last Week",
    getValue: () => {
      const lastWeek = startOfToday().subtract(1, "week");
      return [lastWeek.startOf("week"), lastWeek.endOf("week")];
    },
  },
  {
    label: "Last 7 Days",
    getValue: () => {
      const today = startOfToday();
      return [today.subtract(6, "day"), today];
    },
  },
  {
    label: "Current Month",
    getValue: () => {
      const today = startOfToday();
      return [today.startOf("month"), today.endOf("month")];
    },
  },
  {
    label: "Next Month",
    getValue: () => {
      const nextMonth = startOfToday().add(1, "month");
      return [nextMonth.startOf("month"), nextMonth.endOf("month")];
    },
  },
  {
    label: "Last 3 Months",
    getValue: () => [
      startOfToday().subtract(3, "month").add(1, "day"),
      startOfToday(),
    ],
  },
  {
    label: "Last 6 Months",
    getValue: () => [
      startOfToday().subtract(6, "month").add(1, "day"),
      startOfToday(),
    ],
  },
  {
    label: "Last 12 Months",
    getValue: () => [
      startOfToday().subtract(1, "year").add(1, "day"),
      startOfToday(),
    ],
  },
  {
    label: "Last Year",
    getValue: () => {
      const previousYear = startOfToday().subtract(1, "year");
      return [previousYear.startOf("year"), previousYear.endOf("year")];
    },
  },
];

export default function FilterDateRangePicker({
  label = "Date range",
  slotProps,
  ...props
}: FilterDateRangePickerProps) {
  const theme = useTheme();
  const textFieldSlotProps =
    typeof slotProps?.textField === "function"
      ? undefined
      : slotProps?.textField;
  const shortcutsSlotProps =
    typeof slotProps?.shortcuts === "function"
      ? undefined
      : slotProps?.shortcuts;
  const desktopPaperSlotProps =
    typeof slotProps?.desktopPaper === "function"
      ? undefined
      : slotProps?.desktopPaper;
  const toolbarSlotProps =
    typeof slotProps?.toolbar === "function" ? undefined : slotProps?.toolbar;
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateRangePicker
        {...props}
        desktopModeMediaQuery="(min-width: 1200px)"
        format="DD/MM/YY"
        label={label}
        localeText={{ ...props.localeText, clearButtonLabel: "Reset" }}
        onOpen={() => {
          props.onOpen?.();
          const activeElement = document.activeElement;
          if (
            activeElement instanceof HTMLElement &&
            activeElement.matches('[data-mui-picker-open-button="true"]')
          ) {
            activeElement.blur();
          }
        }}
        slotProps={{
          ...slotProps,
          actionBar: {
            actions: ["clear"],
            sx: {
              justifyContent: "flex-end",
              padding: "8px 12px 12px",
              "& .MuiButton-root": {
                color: "#d32f2f",
                fontWeight: 600,
              },
            },
          },
          field: { clearable: false, ...slotProps?.field },
          desktopPaper: {
            ...desktopPaperSlotProps,
            sx: {
              "& .MuiPickersLayout-root": {
                gridAutoRows: "max-content max-content max-content",
              },
              "& .MuiDayCalendar-root": {
                minHeight: 0,
              },
              "& .MuiDayCalendar-slideTransition": {
                minHeight: "160px",
              },
              "& .MuiDayCalendar-slideTransition:has(.MuiDayCalendar-weekContainer:nth-of-type(5))":
                {
                  minHeight: "200px",
                },
              "& .MuiDayCalendar-slideTransition:has(.MuiDayCalendar-weekContainer:nth-of-type(6))":
                {
                  minHeight: "240px",
                },
              ...(!Array.isArray(desktopPaperSlotProps?.sx) &&
              typeof desktopPaperSlotProps?.sx !== "function"
                ? desktopPaperSlotProps?.sx
                : {}),
            },
          },
          mobilePaper: {
            ...slotProps?.mobilePaper,
            sx: {
              width: "calc(100% - 32px)",
              maxWidth: "480px",
              height: "auto",
              maxHeight: "calc(100% - 32px)",
              overflowY: "auto",
              "& .MuiPickersLayout-root": {
                gridTemplateColumns: "minmax(0, 1fr)",
                gridAutoColumns: "minmax(0, 1fr)",
                gridAutoRows: "max-content",
                width: "100%",
              },
              "&& .MuiPickersLayout-toolbar": {
                gridColumn: "1 / 2",
                gridRow: 1,
              },
              "&& .MuiPickersLayout-shortcuts": {
                gridColumn: "1 / 2",
                gridRow: 2,
                minWidth: 0,
                maxWidth: "100%",
              },
              "&& .MuiPickersLayout-contentWrapper": {
                gridColumn: "1 / 2",
                gridRow: 3,
                minWidth: 0,
                justifySelf: "center",
              },
              "&& .MuiPickersLayout-actionBar": {
                gridColumn: "1 / 2",
                gridRow: 4,
              },
              "& .MuiTypography-root": {
                fontSize: "0.875rem",
              },
              "& .MuiPickersCalendarHeader-label": {
                fontSize: "1rem",
                fontWeight: 600,
              },
              "& .MuiDayCalendar-root": {
                minHeight: 0,
              },
              "& .MuiDayCalendar-slideTransition": {
                minHeight: "160px",
              },
              "& .MuiDayCalendar-slideTransition:has(.MuiDayCalendar-weekContainer:nth-of-type(5))":
                {
                  minHeight: "200px",
                },
              "& .MuiDayCalendar-slideTransition:has(.MuiDayCalendar-weekContainer:nth-of-type(6))":
                {
                  minHeight: "240px",
                },
              "& .MuiDayCalendar-weekDayLabel": {
                fontSize: "0.75rem",
              },
              "& .MuiPickersDay-root": {
                width: 36,
                height: 36,
                fontSize: "0.875rem",
              },
              "& .MuiChip-label": {
                fontSize: "0.8125rem",
              },
              ...(!Array.isArray(slotProps?.mobilePaper?.sx) &&
              typeof slotProps?.mobilePaper?.sx !== "function"
                ? slotProps?.mobilePaper?.sx
                : {}),
            },
          },
          shortcuts: {
            items: dateRangeShortcuts,
            ...shortcutsSlotProps,
            sx: {
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              alignContent: "start",
              maxWidth: "320px",
              "& .MuiListItem-root": {
                minWidth: 0,
                padding: "4px 6px",
              },
              "& .MuiChip-root": {
                width: "100%",
                height: "28px",
                border: "1px solid #DFE5EF",
                borderRadius: "999px",
                backgroundColor: "#F3F4F7",
                color: "#2A3547",
                fontWeight: 500,
              },
              "& .MuiChip-label": {
                paddingInline: "10px",
                fontSize: "0.75rem",
                lineHeight: 1,
              },
              [theme.breakpoints.down("lg")]: {
                display: "flex",
                gridTemplateColumns: "none",
                flexWrap: "wrap",
                width: "100%",
                maxWidth: "100%",
                overflowX: "hidden",
                overflowY: "visible",
                justifyContent: "flex-start",
                boxSizing: "border-box",
                padding: "4px 6px",
                "& .MuiListItem-root": {
                  flex: "1 1 128px",
                  minWidth: 0,
                  width: "auto",
                  padding: "4px",
                },
                "& .MuiChip-root": {
                  width: "100%",
                },
              },
              ...(!Array.isArray(shortcutsSlotProps?.sx) &&
              typeof shortcutsSlotProps?.sx !== "function"
                ? shortcutsSlotProps?.sx
                : {}),
            },
          },
          toolbar: {
            ...toolbarSlotProps,
            toolbarFormat: "DD/MM/YY",
            sx: {
              height: "auto",
              justifyContent: "flex-start",
              gap: "8px",
              padding: "12px 16px 8px",
              "& .MuiPickersToolbar-content": {
                flex: "0 0 auto",
              },
              "& .MuiDateRangePickerToolbar-container": {
                alignItems: "center",
                flexWrap: "nowrap",
              },
              "& .MuiDateRangePickerToolbar-container > .MuiTypography-root": {
                alignSelf: "center",
                lineHeight: 1,
                marginInline: "8px",
              },
              "& .MuiPickersToolbarText-root": {
                fontSize: "1rem",
                lineHeight: 1.4,
              },
              ...(!Array.isArray(toolbarSlotProps?.sx) &&
              typeof toolbarSlotProps?.sx !== "function"
                ? toolbarSlotProps?.sx
                : {}),
            },
          },
          textField: {
            fullWidth: true,
            size: "small",
            ...textFieldSlotProps,
            sx: {
              "&& .MuiPickersInputBase-root, && .MuiPickersOutlinedInput-root":
                {
                  minHeight: "44px !important",
                  height: "44px !important",
                  borderRadius: "4px !important",
                },
              "& .MuiPickersInputBase-sectionsContainer": {
                minWidth: 0,
                flex: "1 1 auto",
              },
              "& .MuiIconButton-root": {
                width: 32,
                height: 32,
                padding: "4px",
              },
              "& .MuiSvgIcon-root": {
                width: 20,
                height: 20,
              },
              "& .MuiInputLabel-root.MuiInputLabel-shrink": {
                display: "none",
              },
              "& .MuiPickersOutlinedInput-notchedOutline legend": {
                maxWidth: 0,
              },
              "& .MuiPickersSectionList-root": {
                fontSize: "0.875rem",
              },
              "& .MuiPickersSectionList-sectionContent": {
                fontSize: "0.875rem",
              },
              ...(!Array.isArray(textFieldSlotProps?.sx) &&
              typeof textFieldSlotProps?.sx !== "function"
                ? textFieldSlotProps?.sx
                : {}),
            },
          },
        }}
        timezone="UTC"
      />
    </LocalizationProvider>
  );
}
